import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TablePaginationConfig } from 'antd/lib/table';
import { useHistory } from 'react-router';

import './index.less';

import { AgentRelationModel, AgentRelationState } from '../../../../../../gamma-api';
import { RELATIONS_PAGE_SIZE } from '../const';
import { getAgentPath } from '../../../../../utils/pathUtils';
import IbIcon from '../../../../../components/common/IbIcon';
import IbButton from '../../../../../components/common/IbButton';
import IbTable from '../../../../../components/common/IbTable';
import IbContextMenu from '../../../../../components/common/IbContextMenu';
import IbPagination from '../../../../../components/common/IbPagination';
import IbTypography from '../../../../../components/common/IbTypography';
import IbTooltip from '../../../../../components/common/IbTooltip';
import IbSwitch from '../../../../../components/common/IbSwitch';

import { MAIN_CLASS_NAME, CELL_CLASS_NAME, PAGINATION_ROW_CLASS_NAME } from './const';

export interface IRelationTableProps {
  dataLoading: boolean;
  pageLoading: boolean;
  relations: AgentRelationModel[];
  pagination: TablePaginationConfig;
  onSetup?: (relation: AgentRelationModel) => void;
  onDelete?: (relation: AgentRelationModel) => void;
  onPaginationChange?: (page: number) => void;
  onRelationStateChange?: (relation: AgentRelationModel, state: AgentRelationState) => void;
}

const RelationTable: React.FC<IRelationTableProps> = ({
  dataLoading,
  pageLoading,
  relations,
  pagination,
  onSetup,
  onDelete,
  onPaginationChange,
  onRelationStateChange,
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();

  const [selectedRelation, setSelectedRelation] = useState<AgentRelationModel>();

  const onMenuClose = () => {
    setSelectedRelation(undefined);
  };

  const renderName = (relation: AgentRelationModel) => (
    <span className={CELL_CLASS_NAME}>
      <IbIcon iconName="user-business" size={20} />
      <IbTooltip title={relation.managed_agent.name}>
        <IbTypography.Paragraph ellipsis type="secondary">
          {relation.managed_agent.name}
        </IbTypography.Paragraph>
      </IbTooltip>
    </span>
  );

  const renderHandoffDescription = (relation: AgentRelationModel) => (
    <span className={CELL_CLASS_NAME}>
      <IbTooltip title={relation.handoff?.description}>
        <IbTypography.Paragraph ellipsis type="secondary">
          {relation.handoff?.description}
        </IbTypography.Paragraph>
      </IbTooltip>
    </span>
  );

  const renderState = (relation: AgentRelationModel) => {
    const onRelationCheckedChange = (checked: boolean) => {
      onRelationStateChange?.(relation, checked ? AgentRelationState.Enabled : AgentRelationState.Disabled);
    };

    return (
      <span className={CELL_CLASS_NAME}>
        <IbSwitch
          checked={relation.state === AgentRelationState.Enabled}
          size="small"
          onChange={onRelationCheckedChange}
        />
        <IbTypography.Paragraph type="secondary">
          {relation.state === AgentRelationState.Enabled
            ? t('Agent: Relation: Table: Enabled')
            : t('Agent: Relation: Table: Disabled')}
        </IbTypography.Paragraph>
      </span>
    );
  };

  const renderContext = (relation: AgentRelationModel) => {
    const onMoreButtonClick = () => {
      setSelectedRelation(relation);
    };

    const onGoToAgentSelect = () => {
      push(
        getAgentPath(
          relation.managed_agent.settings.interface.type,
          relation.managed_agent.folder_id,
          relation.managed_agent.agent_id
        )
      );
    };

    const onSetupSelect = () => {
      onSetup?.(relation);
    };

    const onDeleteSelect = () => {
      onDelete?.(relation);
    };

    const menuItems = [
      {
        icon: <IbIcon iconName="link-right" size={20} />,
        text: t('Agent: Relation: Table: Go to agent'),
        onSelect: onGoToAgentSelect,
      },
      {
        icon: <IbIcon iconName="setting-two" size={20} />,
        text: t('Agent: Relation: Table: Setup'),
        onSelect: onSetupSelect,
      },
      {
        icon: <IbIcon iconName="delete" size={20} />,
        text: t('Agent: Relation: Table: Delete'),
        onSelect: onDeleteSelect,
      },
    ];

    return (
      <IbContextMenu
        menuItems={menuItems}
        visible={relation.relation_id == selectedRelation?.relation_id}
        onClose={onMenuClose}
      >
        <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onMoreButtonClick} />
      </IbContextMenu>
    );
  };

  const columns = [
    {
      title: t('Agent: Relation: Table: Name'),
      render: renderName,
      ellipsis: true,
    },
    {
      title: t('Agent: Relation: Table: Handoff description'),
      render: renderHandoffDescription,
      ellipsis: true,
      width: '50%',
    },
    {
      title: t('Agent: Relation: Table: State'),
      render: renderState,
    },
    {
      title: '',
      render: renderContext,
      width: 40,
    },
  ];

  return (
    <>
      <IbTable
        className={MAIN_CLASS_NAME}
        columns={columns}
        dataSource={relations}
        loading={dataLoading || pageLoading}
        rowKey="relation_id"
      />
      {!dataLoading && (pagination.total ?? 0) > RELATIONS_PAGE_SIZE && (
        <div className={PAGINATION_ROW_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={RELATIONS_PAGE_SIZE}
            pageSize={RELATIONS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
    </>
  );
};

export default RelationTable;
